import React, { useEffect, useState } from "react";
import "../styles/MessageModal.css";
import "../styles/AdminMessages.css";

export default function EditUserMessageModal({ isOpen, onClose, message, onMessageUpdated }) {
    const [allUsers, setAllUsers] = useState([]);
    const [formData, setFormData] = useState({
        title: "",
        content: "",
        message_type: "info",
        selectedUsers: []
    });
    const [userSearchTerm, setUserSearchTerm] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        
        // Fetch all users
        const fetchUsers = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${process.env.REACT_APP_API_URL}/mail/users`, {
                    headers: { Authorization: `Bearer ${token}` },
                    credentials: 'include'
                });
                if (response.ok) {
                    const data = await response.json();
                    setAllUsers(data);
                }
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };
        
        fetchUsers();
        
        // Set form data from message
        setFormData({
            title: message.title || "",
            content: message.content || "",
            message_type: message.message_type || "info",
            selectedUsers: message.recipient_users ? message.recipient_users.map(u => u.user_id) : []
        });
    }, [isOpen, message]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((p) => ({ ...p, [name]: value }));
    };

    const handleUserToggle = (userId) => {
        setFormData((prev) => {
            if (prev.selectedUsers.includes(userId)) {
                return { ...prev, selectedUsers: prev.selectedUsers.filter(id => id !== userId) };
            } else {
                return { ...prev, selectedUsers: [...prev.selectedUsers, userId] };
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.selectedUsers.length === 0) {
            alert('Proszę wybrać przynajmniej jednego użytkownika.');
            return;
        }
        
        try {
            const token = localStorage.getItem('token');
            const headers = { "Content-Type": "application/json" };
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }

            const res = await fetch(`${process.env.REACT_APP_API_URL}/messages/${message.message_id}`, {
                method: "PUT",
                headers: headers,
                credentials: 'include',
                body: JSON.stringify({
                    title: formData.title,
                    content: formData.content,
                    message_type: formData.message_type,
                    selectedUsers: formData.selectedUsers
                }),
            });
            if (res.ok) {
                onMessageUpdated();
                onClose();
            } else {
                console.error("Błąd przy aktualizacji wiadomości");
            }
        } catch (err) {
            console.error("Błąd przy aktualizacji wiadomości:", err);
        }
    };

    if (!isOpen) return null;

    return (
        <div className='user-selector-modal-overlay' onClick={(e) => e.currentTarget === e.target && onClose()}>
            <div className='user-message-modal' onClick={(e) => e.stopPropagation()}>
                <div className='user-selector-header'>
                    <h3>Edytuj powiadomienie do użytkowników</h3>
                    <button 
                        className='user-selector-close'
                        onClick={onClose}
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    <div className='user-message-modal-content'>
                        {/* Message Type */}
                        <div className='user-message-section'>
                            <label className='form-label'>Typ powiadomienia</label>
                            <select
                                className='form-input'
                                name="message_type"
                                value={formData.message_type}
                                onChange={handleChange}
                            >
                                <option value='info'>Informacja</option>
                                <option value='warning'>Ważne</option>
                                <option value='alert'>Alert</option>
                            </select>
                        </div>

                        {/* Message Subject */}
                        <div className='user-message-section'>
                            <label className='form-label'>Temat wiadomości</label>
                            <input 
                                type='text'
                                className='form-input'
                                name="title"
                                placeholder='Np. Ważna informacja'
                                value={formData.title}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        {/* Message Content */}
                        <div className='user-message-section'>
                            <label className='form-label'>Treść</label>
                            <textarea 
                                className='form-textarea'
                                name="content"
                                placeholder='Treść wiadomości...'
                                value={formData.content}
                                onChange={handleChange}
                                rows={6}
                                required
                            />
                        </div>

                        {/* User Selection */}
                        <div className='user-message-section'>
                            <label className='form-label'>Odbiorcy</label>
                            <div className='user-selector-search'>
                                <input
                                    type='text'
                                    placeholder='Szukaj użytkownika...'
                                    value={userSearchTerm}
                                    onChange={(e) => setUserSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className='user-selector-list' style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {allUsers
                                    .filter(user => {
                                        const searchLower = userSearchTerm.toLowerCase();
                                        return !userSearchTerm || 
                                            user.full_name?.toLowerCase().includes(searchLower) ||
                                            user.username?.toLowerCase().includes(searchLower) ||
                                            user.email?.toLowerCase().includes(searchLower);
                                    })
                                    .map(user => (
                                        <label key={user.user_id} className='user-selector-item'>
                                            <input
                                                type='checkbox'
                                                checked={formData.selectedUsers.includes(user.user_id)}
                                                onChange={() => handleUserToggle(user.user_id)}
                                            />
                                            <div className='user-selector-item-info'>
                                                <div className='user-selector-item-name'>{user.full_name || user.username}</div>
                                                <div className='user-selector-item-details'>
                                                    {user.email && <span>{user.email}</span>}
                                                    <span className={`user-type-badge user-type-${user.user_type}`}>
                                                        {user.user_type === 'student' ? 'Kursant' : 
                                                         user.user_type === 'lecturer' ? 'Wykładowca' : 
                                                         user.user_type === 'administrator' ? 'Administrator' : user.user_type}
                                                    </span>
                                                    {user.group_name && <span>Grupa: {user.group_name}</span>}
                                                </div>
                                            </div>
                                        </label>
                                    ))}
                            </div>
                            {formData.selectedUsers.length > 0 && (
                                <div style={{ marginTop: '10px', fontSize: '0.9em', color: '#666', padding: '0.5rem', background: '#f8fafc', borderRadius: '6px' }}>
                                    Wybrano: {formData.selectedUsers.length} {formData.selectedUsers.length === 1 ? 'użytkownika' : 'użytkowników'}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className='user-selector-footer'>
                        <button 
                            type='button'
                            className='btn btn-secondary'
                            onClick={onClose}
                        >
                            Anuluj
                        </button>
                        <button 
                            type='submit'
                            className='btn btn-primary'
                            disabled={formData.selectedUsers.length === 0}
                        >
                            Zapisz zmiany ({formData.selectedUsers.length})
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

